// 📁 src/services/voiceApiService.js
// 🎛️ Voice API Service - Consumo directo de Voice Core (puerto 3003)
// Variante A: Frontend → Voice Core directo vía /api/voice

/**
 * Servicio para interacción con Voice Core EPL (puerto 3003)
 * Usa SIEMPRE rutas relativas para funcionar en dev y prod sin cambios
 */

const VOICE_BASE_PATH = '/api/voice/v1';

/**
 * Convierte texto a audio usando TTS (soporta Voice Roles System)
 * @param {Object} params - Parámetros de TTS
 * @param {string} params.text - Texto a convertir
 * @param {string} [params.voice] - Voz a usar (opcional, el backend usa products.config.js)
 * @param {string} [params.model] - Modelo TTS (opcional)
 * @param {string} [params.format] - Formato de audio (opcional)
 * @param {number} [params.speed] - Velocidad de reproducción (opcional)
 * @param {string} [params.agentId] - ID del agente (Voice Roles System)
 * @param {string} [params.voiceRole] - Rol de voz: 'narrator', 'client', 'coach' (Voice Roles System)
 * @returns {Promise<{audioBase64: string, contentType: string}>}
 */
export async function textToSpeech({ text, voice, model, format, speed, agentId, voiceRole }) {
  try {
    // 🔧 MULTI-TENANT: Solo enviar parámetros explícitos (no defaults)
    const body = { text };
    if (voice !== undefined) body.voice = voice;
    if (model !== undefined) body.model = model;
    if (format !== undefined) body.format = format;
    if (speed !== undefined) body.speed = speed;
    
    // 🎭 VOICE ROLES SYSTEM: Enviar agentId y voiceRole si existen
    if (agentId !== undefined) body.agentId = agentId;
    if (voiceRole !== undefined) body.voiceRole = voiceRole;
    
    // 🔍 DEBUG: Log de parámetros Voice Roles
    if (agentId || voiceRole) {
      const roleEmoji = voiceRole === 'narrator' ? '📖' : 
                       voiceRole === 'coach' ? '🎓' : '🎭';
      console.log(`${roleEmoji} [Voice Roles] agentId: ${agentId || 'default'}, role: ${voiceRole || 'client'}`);
    }
    
    const response = await fetch(`${VOICE_BASE_PATH}/tts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Product-Id': 'vertex-quick'  // 🔧 CRITICAL: Identificador para multi-tenant TTS
      },
      body: JSON.stringify(body),
    });

    const data = await response.json();

    // 🔍 DEBUG: Log raw response
    console.log('🔍 [TTS DEBUG] Response status:', response.status);
    console.log('🔍 [TTS DEBUG] Response ok:', response.ok);
    console.log('🔍 [TTS DEBUG] Data keys:', Object.keys(data));

    if (!response.ok) {
      console.error('❌ [TTS DEBUG] Error response:', data);
      throw new Error(data.error || 'Error al generar audio');
    }

    // Verificar que tenga audioBase64
    if (!data.audioBase64) {
      console.error('❌ [TTS DEBUG] No audioBase64 en respuesta:', data);
      throw new Error('Respuesta inválida del servidor (falta audioBase64)');
    }

    // 🔍 LOG PROVIDER DEBUG
    const provider = data.provider || 'unknown';
    const providerLabel = provider === 'google' ? '🟢 REPRODUCE GEMINI' : '🔵 REPRODUCE OPENAI';
    console.log(`%c${providerLabel}`, 'font-size: 16px; font-weight: bold; color: ' + (provider === 'google' ? 'green' : 'blue'));
    console.log('📊 Provider:', provider);
    console.log('🎙️ Voice:', data.voice);
    console.log('📦 Size:', data.sizeKB + 'KB');
    console.log('⏱️ Duration:', data.duration_ms + 'ms');

    return data; // { audioBase64, contentType, sizeKB, duration_ms, provider, voice }
  } catch (error) {
    console.error('[VoiceApiService] Error en TTS:', error);
    throw error;
  }
}

/**
 * Convierte audio a texto usando STT
 * @param {FormData} formData - FormData con el archivo de audio
 * @returns {Promise<{text: string}>}
 */
export async function speechToText(formData) {
  try {
    const response = await fetch(`${VOICE_BASE_PATH}/stt`, {
      method: 'POST',
      body: formData,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Error al transcribir audio');
    }

    return data; // { text, language, duration_ms, audioSizeKB }
  } catch (error) {
    console.error('[VoiceApiService] Error en STT:', error);
    throw error;
  }
}

export default {
  textToSpeech,
  speechToText,
};
